import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Job,
  jobsService,
  EngagePostMessageActions,
  SendMessageToSelf,
  useIframeEngageActionListener,
} from '../services';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import {
  AddPhotosButton,
  SearchBoxHeader,
  SelectJobDialogContent,
} from './select-job-modal.styles';

interface SelectJobModalProps {
  engageOrigin: string;
  accessToken: string;
}

export const SelectJobModal: FunctionComponent<SelectJobModalProps> = ({
  engageOrigin,
  accessToken,
}) => {
  const eventBody = useIframeEngageActionListener({
    engageOrigin,
    action: EngagePostMessageActions.OPEN_SELECT_JOB_MODAL,
  });
  const [open, setOpen] = useState(false);
  const [optionsExist, setOptionsExist] = useState(true);
  const [contactId, setContactId] = useState('');
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [options, setOptions] = useState<readonly Job[]>([]);
  const loading = useMemo(
    () => openAutocomplete && optionsExist && options.length === 0,
    [openAutocomplete, options, optionsExist]
  );
  const [addPhotosDisabled, setAddPhotosDisabled] = useState(true);

  const onChangeJobId = (e, newValue) => {
    const id = newValue.jobId;

    SendMessageToSelf(
      {
        REQUEST_ACTION: {
          type: EngagePostMessageActions.OPEN_ATTACH_FILE_DIALOG,
          associationId: id,
          associationType: 'job',
        },
      },
      true
    );

    handleClose();
  };

  useEffect(() => {
    setOpen(eventBody != null);

    if (eventBody) {
      if (eventBody.associationType === 'contact') {
        setContactId(eventBody.associationId);
      }
    }
  }, [eventBody]);

  useEffect(() => {
    const setJobsByContactId = async () => {
      const response = await jobsService.getJobsByContactId(
        engageOrigin,
        accessToken,
        contactId
      );

      setOptionsExist(response.length > 0);
      setOptions(response);
    };

    let active = true;

    if (!loading) {
      return;
    }

    if (active) {
      setJobsByContactId();
    }

    return () => {
      active = false;
    };
  }, [loading, contactId, accessToken, engageOrigin]);

  useEffect(() => {
    if (!openAutocomplete) {
      setOptions([]);
    }
  }, [openAutocomplete]);

  const handleClose = () => {
    setOpen(false);
    setAddPhotosDisabled(true);
    setOpenAutocomplete(false);
    setOptions([]);
    setContactId('');
    setOptionsExist(true);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="select-job-title"
        aria-describedby="select-job-description"
        data-e2e="select-job-modal"
      >
        <DialogTitle id="select-job-title">
          {'Upload Photo from Job'}
        </DialogTitle>
        <DialogContent>
          <SelectJobDialogContent>
            <SearchBoxHeader>Search job</SearchBoxHeader>
            <Autocomplete
              fullWidth
              disablePortal={true}
              options={options}
              open={openAutocomplete}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              getOptionLabel={(option) => (option as Job).name}
              loading={loading}
              onChange={onChangeJobId}
              data-e2e="search-jobs-autocomplete"
              onOpen={() => {
                setOpenAutocomplete(true);
              }}
              onClose={() => {
                setOpenAutocomplete(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  label="Enter name or search job"
                  data-e2e="search-jobs"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </SelectJobDialogContent>
        </DialogContent>
        <DialogActions>
          <Button data-e2e="cancel-upload-job-photo" onClick={handleClose}>
            Cancel
          </Button>
          {/* Intentionally disabled but kept to align with the modal flow */}
          <AddPhotosButton
            disabled={addPhotosDisabled}
            data-e2e="add-job-photo"
          >
            Add Photo(s)
          </AddPhotosButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
